/// <amd-module name="Wit/Common/Controls/Boards/ValidationError" />

import * as React from "react";
import { IVssPageContext } from "VSS/Platform/Context";
import { usePageContext } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { Observer } from "VSSUI/Observer";
import { Tooltip } from "VSSUI/TooltipEx";
import { IClientWorkItem, IObservableClientWorkItem } from "Wit/ClientWorkItemService/IClientWorkItem";
import * as Resources from "Wit/Common/Controls/Boards/Resources";
import { IWorkItem, IWorkItemInfoText } from "Wit/WorkItemModel/OM/WorkItemInterfaces";

/**
 * Observes the given work item, and if it has a validation error, shows it. Intended for use in
 * onRenderAdditionalContent of Card.
 */
export function ValidationError({
    observableItem,
    onOpenWorkItemForm
}: {
    observableItem: IObservableClientWorkItem;
    onOpenWorkItemForm: (pageContext: IVssPageContext, item: IClientWorkItem) => void;
}): JSX.Element {
    return (
        <>
            <Observer iWorkItem={observableItem.value.iWorkItem}>
                {({ iWorkItem }: { iWorkItem: IWorkItem | undefined }) =>
                    iWorkItem ? (
                        <Observer infoText={iWorkItem?.infoText}>
                            {({ infoText }: { infoText: IWorkItemInfoText | undefined }) =>
                                infoText?.invalid ? (
                                    <ValidationErrorContent infoText={infoText} item={observableItem} onOpenWorkItemForm={onOpenWorkItemForm} />
                                ) : null
                            }
                        </Observer>
                    ) : null
                }
            </Observer>
        </>
    );
}

/**
 * Renders the actual content of the validation error shown when a work item is invalid. Implemented as a separate
 * component from ValidationError so that we only use state in the unlikely event that a work item is invalid.
 */
function ValidationErrorContent({
    infoText,
    item,
    onOpenWorkItemForm
}: {
    infoText: IWorkItemInfoText;
    item: IObservableClientWorkItem;
    onOpenWorkItemForm: (pageContext: IVssPageContext, item: IClientWorkItem) => void;
}) {
    const pageContext = usePageContext();
    const [showDialog, setShowDialog] = React.useState(false);
    const closeDialog = React.useCallback((e?: React.MouseEvent | React.KeyboardEvent) => {
        setShowDialog(false);
        e?.preventDefault();
    }, []);
    return (
        <div
            aria-label={infoText.text}
            role={"alert"}
            className="validation-error absolute"
            onClick={e => {
                if (!e.defaultPrevented) {
                    setShowDialog(true);
                }
            }}
        >
            <Tooltip text={infoText.text}>
                <div className="text-center">{Resources.Error}</div>
            </Tooltip>
            {showDialog && (
                <Dialog
                    footerButtonProps={[
                        {
                            text: Resources.IgnoreButton,
                            onClick: closeDialog
                        },
                        {
                            primary: true,
                            text: Resources.EditItemButton,
                            onClick: () => {
                                onOpenWorkItemForm(pageContext, item.value);
                                setShowDialog(false);
                            }
                        }
                    ]}
                    onDismiss={closeDialog}
                    titleProps={{ text: Resources.ValidationDialogTitle }}
                >
                    <div>{infoText.text}</div>
                </Dialog>
            )}
        </div>
    );
}
